/**
 * ResetPasswordPage
 * =================
 *
 * Two-step password reset.
 *
 * Step 1 (request): user enters email, we call
 * ``supabase.auth.resetPasswordForEmail`` with a redirect back to this
 * same page. Supabase sends a one-time link.
 *
 * Step 2 (set new): user clicks the link, lands here with a recovery
 * session (Supabase exchanges the URL fragment for a session via
 * ``detectSessionInUrl=true``). We listen for the ``PASSWORD_RECOVERY``
 * event and show the "new password" form. On submit we call
 * ``supabase.auth.updateUser``.
 */
import { useEffect, useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../auth/supabase";
import AuthShell from "../components/AuthShell";

export default function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    // Supabase auto-detects the recovery token from the URL on mount;
    // the listener fires PASSWORD_RECOVERY in that case.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setRecoveryMode(true);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function requestReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    setRequestSent(true);
  }

  async function updatePassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    nav("/uploads", { replace: true });
  }

  if (recoveryMode) {
    return (
      <AuthShell title="Set a new password">
        <form onSubmit={updatePassword} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <input className="input" type="password" autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   minLength={6} required />
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <button className="btn-primary w-full" disabled={busy} type="submit">
            {busy ? "Saving…" : "Save new password"}
          </button>
        </form>
      </AuthShell>
    );
  }

  if (requestSent) {
    return (
      <AuthShell title="Check your inbox">
        <p className="text-sm text-slate-600">
          If <strong>{email}</strong> matches an account, we've sent a reset link.
          Click it to set a new password.
        </p>
        <div className="mt-4">
          <Link to="/login" className="btn-primary w-full text-center">
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password">
      <form onSubmit={requestReset} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 required />
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button className="btn-primary w-full" disabled={busy} type="submit">
          {busy ? "Sending link…" : "Send reset link"}
        </button>
      </form>
      <div className="mt-4 text-xs text-slate-500 text-center">
        Remembered it? <Link to="/login" className="hover:text-accent-600">Back to sign in</Link>
      </div>
    </AuthShell>
  );
}
